import type { InferredVisualization, VisualizationType } from "../types";

const isDateValue = (value: unknown): boolean => {
  if (value instanceof Date) return true;
  if (typeof value !== "string") return false;

  // Check for common date patterns
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // ISO date: 2024-01-15
    /^\d{4}\/\d{2}\/\d{2}/, // Slash date: 2024/01/15
    /^\d{2}\/\d{2}\/\d{4}/, // US date: 01/15/2024
  ];

  if (!datePatterns.some((pattern) => pattern.test(value))) {
    return false;
  }

  const parsed = new Date(value);
  return !isNaN(parsed.getTime());
};

const findNumericKeys = (obj: Record<string, unknown>): string[] => {
  return Object.keys(obj).filter((key) => key !== "_id" && typeof obj[key] === "number");
};

const findTimestampKey = (obj: Record<string, unknown>): string | undefined => {
  // Priority order: common date field names first
  const dateKeyPriority = ["date", "timestamp", "time", "createdAt", "updatedAt", "day", "month", "year"];

  for (const key of dateKeyPriority) {
    if (key in obj && isDateValue(obj[key])) {
      return key;
    }
  }

  // Fallback: check all keys
  return Object.keys(obj).find((key) => isDateValue(obj[key]));
};

const findLabelKey = (results: Array<Record<string, unknown>>): string | undefined => {
  // Check if _id exists and has string/number values in ANY row
  if (results.some((obj) => "_id" in obj && (typeof obj._id === "string" || typeof obj._id === "number"))) {
    return "_id";
  }

  const firstObj = results[0];
  if (!firstObj) return undefined;

  // Then check for common label field names
  const labelKeyPriority = ["name", "label", "category", "type", "status", "group"];
  for (const key of labelKeyPriority) {
    if (key in firstObj && typeof firstObj[key] === "string") {
      return key;
    }
  }

  // Fallback: first string key
  return Object.keys(firstObj).find((key) => typeof firstObj[key] === "string");
};

const PIE_CHART_MAX_ITEMS = 6;

export const inferVisualizationType = (
  results: Array<Record<string, unknown>> | null | undefined
): InferredVisualization => {
  // Rule 1: Empty or null results
  if (!results || results.length === 0) {
    return { type: "table" };
  }

  const firstObj = results[0];
  if (!firstObj) return { type: "table" };
  const keys = Object.keys(firstObj);

  // Rule 2: Singleton array with single numeric value → Single stat
  if (results.length === 1) {
    const numericKeys = findNumericKeys(firstObj);

    // If exactly one numeric key, it's a single stat
    if (numericKeys.length === 1) {
      const dataKey = numericKeys[0];
      if (!dataKey) return { type: "table" };
      // Check for grouping key (_id) to include as label
      const labelKey = "_id" in firstObj ? "_id" : undefined;
      return {
        type: "single-stat",
        dataKey,
        ...(labelKey && { labelKey }),
      };
    }

    // If the result has just one key-value pair and it's numeric
    const firstKey = keys[0];
    if (keys.length === 1 && firstKey && typeof firstObj[firstKey] === "number") {
      return {
        type: "single-stat",
        dataKey: firstKey,
      };
    }
  }

  // Rule 3: Check for timestamp fields → Time-series
  const timestampKey = findTimestampKey(firstObj);

  if (timestampKey && results.length > 1) {
    const numericKeys = findNumericKeys(firstObj);
    const dataKey = numericKeys[0];

    if (numericKeys.length > 0 && dataKey) {
      return {
        type: "time-series",
        timestampKey,
        valueKeys: numericKeys,
        dataKey,
      };
    }
  }

  // Rule 4: Multiple objects with grouping key and numeric values → Pie or Bar
  if (results.length > 1) {
    const labelKey = findLabelKey(results);
    const numericKeys = findNumericKeys(firstObj);
    const dataKey = numericKeys[0];

    if (labelKey && numericKeys.length > 0 && dataKey) {
      const vizType: VisualizationType = results.length <= PIE_CHART_MAX_ITEMS ? "pie-chart" : "bar-chart";

      return {
        type: vizType,
        labelKey,
        dataKey,
        valueKeys: numericKeys,
      };
    }
  }

  // Rule 5: Fallback to table
  return { type: "table" };
};
